import React from "react";
import { Users } from "lucide-react";
import { motion } from "framer-motion";

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6 },
  }),
};

const FeaturesSection = () => {
  return (
    <section className="relative py-10 px-4 md:px-10 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
      
      {/* Background Shapes */}
      <div className="absolute -top-10 -left-10 w-60 h-60 bg-blue-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Top Title + Button */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight">
            <span className="bg-gradient-to-r from-blue-600 to-blue-400 text-transparent bg-clip-text">
              Offering You Premium
            </span>
            <br />
            <span className="text-gray-900">Best Features</span>
          </h2>

          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl text-white font-semibold text-sm px-8 py-3 rounded-full transition-all shadow-lg"
          >
            Explore more
          </motion.button>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Feature Cards */}
          <div className="grid grid-cols-2 md:grid-cols-2 border border-blue-300 rounded-3xl overflow-hidden shadow-xl bg-white/70 backdrop-blur-sm">
            
            {[
              {
                title: "Visa Support",
                text: "End-to-end support for smooth and stress-free visa processing.",
              },
              {
                title: "Expert Team",
                text: "A team of visa professionals guiding you through every step.",
              },
              {
                title: "Immigration",
                text: "Legal process of settling in another country permanently.",
              },
              {
                title: "98% Success",
                text: "Trusted by thousands with a 98% success track record.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
                whileHover={{
                  scale: 1.02,
                  backgroundColor: "rgba(59,130,246,0.2)",
                  transition: { duration: 0.3 },
                }}
                className={`md:p-8 p-5 flex flex-col gap-4 border-blue-200 ${
                  i % 2 === 0 ? "border-r" : ""
                } ${i < 2 ? "md:border-b" : ""}`}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center shadow-inner">
                  <Users size={26} strokeWidth={2.5} className="text-blue-700" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full md:h-[70vh] h-[40vh] flex justify-center lg:justify-end"
          >
            <motion.img
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.4 }}
              src="https://images.pexels.com/photos/1098515/pexels-photo-1098515.jpeg"
              alt="Features"
              className="rounded-3xl shadow-2xl w-full max-w-md lg:max-w-full object-cover"
            />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
